import { type SearchCategory, type SearchCategoryAlias, isSearchCategory} from '@mimir/common/dto/searchModels';
import { ItemType } from '@mimir/common/dto/itemTypes';
import { MediaAvailabilityStatus } from '@mimir/common/dto/mediaDtos';
import { reactive } from '../compat/vueCompat';
import cloneDeep from 'lodash/cloneDeep';

export const MediaTypes: SearchCategoryAlias[] = [ItemType.VIDEO, ItemType.IMAGE, ItemType.AUDIO, ItemType.FILE, 'sequence', ItemType.GRAPHICS];
export const NonMediaTypes = [MediaAvailabilityStatus.STREAMING, ItemType.FOLDER, ItemType.PERSON];
export const MEDIA_FILTER = 'media';
export const ALL_FILTER = 'all';

export type MediaType = typeof MediaTypes[number];

export type SearchCategoryUI = SearchCategoryAlias | typeof MEDIA_FILTER | typeof ALL_FILTER;

interface FilterState {
  radioFilter: SearchCategoryUI;
  selectedMediaTypes: SearchCategoryAlias[];
}

export const filters: FilterState = reactive({
  radioFilter: MEDIA_FILTER,
  selectedMediaTypes: getInitialMediaTypes(),
});

export const setRadioFilter = (filter: SearchCategoryUI) => {
  filters.radioFilter = filter;
};

export const setSelectedMediaTypes = (type: SearchCategoryAlias | SearchCategoryAlias []) => {
  if (Array.isArray(type)) {
    filters.selectedMediaTypes = cloneDeep(type);
  } else {
    filters.selectedMediaTypes.push(type);
  }
};

export const clearOrRemoveSelectedMediaTypes = (type?: SearchCategoryUI) => {
  if (!type) {
    return filters.selectedMediaTypes = [];
  }
  if (!isSearchCategory(type)) {
    return;
  }
  const index = filters.selectedMediaTypes.indexOf(type);
  if (index !== -1) {
    filters.selectedMediaTypes.splice(index, 1);
  }
};

function getInitialMediaTypes(): MediaType[] {
  if (typeof window === 'undefined') {
    return cloneDeep(MediaTypes);
  }

  const params = new URLSearchParams(window.location.search);
  const raw = params.get('searchString');
  if (!raw) {
    return cloneDeep(MediaTypes);
  }

  // Split into individual filter tokens (e.g. ["itemState:complete", "type:video,image"])
  const tokens = raw.split(/\s+/);
  const typeToken = tokens.find(token => token.startsWith('type:'));
  if (!typeToken) {
    return cloneDeep(MediaTypes);
  }
  // Extract the list of media filter types (e.g. ["video", "image"])
  const list = typeToken.slice('type:'.length);
  if (!list) {
    return cloneDeep(MediaTypes);
  }

  const pickedAllFilterType = list.split(',').filter(isSearchCategory) as SearchCategory[];

  const mediaTypes = new Set<MediaType>();
  pickedAllFilterType.forEach(c => {
    if (c === 'clipList' || c === 'timeline') {
      mediaTypes.add('sequence');
    } else {
      mediaTypes.add(c);
    }
  });

  return mediaTypes.size !== 0 ? [...mediaTypes] : cloneDeep(MediaTypes);
}
